import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { useGetMaintenanceRequestQuery } from "../../slices/maintenanceApiSlice";
import { useGetAssetQuery } from "../../slices/assetApiSlice";
import { useGetAcademyQuery } from "../../slices/userApiSlice";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ChevronDown,
  Download,
  Eye,
  FileText,
  Calendar,
  Building2,
  MapPin,
  Map,
  RefreshCw,
  Hash,
  Box,
} from "lucide-react";

const AdminMaintenance = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedWorkStatus, setSelectedWorkStatus] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const status = searchParams.get("status");
    if (status) {
      setSelectedWorkStatus(status);
    } else {
      setSelectedWorkStatus("");
    }
  }, [searchParams]);
  const [selectedLocation, setSelectedLocation] = useState("");

  const [selectedMaintenance, setSelectedMaintenance] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const {
    data: maintenanceRequest,
    isLoading,
    refetch: refetchMaintenanceRequest,
  } = useGetMaintenanceRequestQuery();
  const { data: assetData, refetch: refetchAssetData } = useGetAssetQuery();
  const { data: academy } = useGetAcademyQuery();

  // Use Sonner for loading state
  useEffect(() => {
    if (isLoading) {
      toast.loading("Loading data, please wait...");
    } else {
      toast.dismiss();
    }
    return () => toast.dismiss();
  }, [isLoading]);

  const rowsPerPage = 10;
  const [data, setData] = useState([]);

  useEffect(() => {
    if (maintenanceRequest && assetData) {
      const filtered = maintenanceRequest
        .map((request) => {
          const matchedAsset = assetData.find(
            (a) => a.assetCode === request.assetCode,
          );

          if (matchedAsset) {
            console.log(
              `Match found for assetCode ${request.assetCode}:`,
              matchedAsset.title,
            );
            return {
              ...request,
              assetName: matchedAsset.title, // Attach the asset name
              academyID: matchedAsset.academyID,
            };
          }

          return null;
        })
        .filter((r) => r !== null);

      const sorted = filtered.sort(
        (a, b) =>
          new Date(b.requestDate || b.startDate) -
          new Date(a.requestDate || a.startDate),
      );

      setData(sorted);
    }
  }, [maintenanceRequest, assetData]);

  const getAcademyName = (academyID) => {
    const match = academy?.find((a) => a.academyId === academyID);
    return match ? match.name : "Unknown";
  };

  // Filtering data based on search and priority selection and work status
  // Remove conflicting sort, use the data already sorted in useEffect
  const sortedData = [...data];

  const filteredData = sortedData.filter((item) => {
    const searchLower = searchTerm.toLowerCase();

    const matchesSearch =
      (item.maintenanceID || item.preventiveMaintenanceID)
        ?.toString()
        .toLowerCase()
        .includes(searchLower) ||
      Object.values(item).some(
        (value) =>
          value != null && value.toString().toLowerCase().includes(searchLower),
      );

    const matchesWorkStatus =
      selectedWorkStatus === "" ||
      item.status?.toLowerCase() === selectedWorkStatus.toLowerCase();

    const matchesLocation =
      selectedLocation === "" ||
      getAcademyName(item.academyID)?.toLowerCase() ===
        selectedLocation.toLowerCase();

    return matchesSearch && matchesWorkStatus && matchesLocation;
  });

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const displayedData = filteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );

  const formatDate = (dateString) => {
    if (!dateString) return "";

    const [year, month, day] = dateString.split("-");
    const safeYear = year ? year.substring(0, 4) : "";
    return `${day}-${month}-${safeYear}`;
  };

  const handleOpenViewModal = (maintenance) => {
    setSelectedMaintenance(maintenance);
    setIsViewModalOpen(true);
  };

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="px-6 py-4">
          <h1 className="text-2xl font-semibold tracking-tight">
            Preventive Maintenance
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage and schedule preventive maintenance tasks and schedules
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="border-b bg-card px-6 py-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              type="text"
              placeholder="Search maintenance..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>


          <select
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
            className="flex h-10 w-full md:w-[180px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="">All Locations</option>
            {Array.from(
              new Set(
                data.map((item) =>
                  getAcademyName(item.academyID)?.toLowerCase(),
                ),
              ),
            ).map((location) => (
              <option key={location} value={location}>
                {location.charAt(0).toUpperCase() + location.slice(1)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto p-6">
        <div className="rounded-md border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="font-semibold">Asset Code</TableHead>
                <TableHead className="font-semibold">Asset Name</TableHead>
                <TableHead className="font-semibold">Description</TableHead>
                <TableHead className="font-semibold">
                  Schedule (Month)
                </TableHead>
                <TableHead className="font-semibold">From Date</TableHead>
                <TableHead className="font-semibold">To Date</TableHead>
                <TableHead className="font-semibold">Work Status</TableHead>
                <TableHead className="font-semibold text-right">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayedData.length > 0 ? (
                displayedData.map((item, index) => (
                  <TableRow key={item.maintenanceID || index}>
                    <TableCell>
                      <Badge variant="outline">{item.assetCode}</Badge>
                    </TableCell>
                    <TableCell className="font-medium">
                      {item.assetName}
                    </TableCell>
                    <TableCell className="max-w-xs truncate">
                      {item.description}
                    </TableCell>
                    <TableCell>{item.repeat}</TableCell>
                    <TableCell>{formatDate(item.startDate)}</TableCell>
                    <TableCell>{formatDate(item.endDate)}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          item.status?.toLowerCase() === "completed"
                            ? "success"
                            : item.status?.toLowerCase() === "inprogress"
                              ? "warning"
                              : "secondary"
                        }
                      >
                        {item.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenViewModal(item)}
                        title="View Details"
                      >
                        <Eye className="h-4 w-4 text-muted-foreground hover:text-primary transition-colors" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="text-center py-8 text-muted-foreground"
                  >
                    No maintenance requests found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 0 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-muted-foreground">
              {filteredData.length} Results
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {/* View Details Modal */}
        <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0 border-0 rounded-xl shadow-2xl">
            {selectedMaintenance && (
              <>
                {/* Header Section */}
                <div className="bg-primary/5 p-6 border-b flex justify-between items-start gap-3">
                  <DialogHeader className="space-y-1 pr-6 flex-1">
                    <DialogTitle className="text-2xl font-bold flex items-center gap-2">
                      <FileText className="w-5 h-5 text-primary" />
                      Preventive Maintenance Details
                    </DialogTitle>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>
                        ID:{" "}
                        {selectedMaintenance.preventiveMaintenanceID ||
                          selectedMaintenance.maintenanceID}
                      </span>
                      <span>•</span>
                      <span>
                        {getAcademyName(selectedMaintenance.academyID)}
                      </span>
                    </div>
                  </DialogHeader>

                  {/* Status Badge */}
                  <div className="flex flex-col items-end gap-2">
                    <Badge
                      className="px-3 py-1 shadow-sm capitalize font-medium text-xs whitespace-nowrap"
                      variant={
                        selectedMaintenance.status?.toLowerCase() ===
                        "completed"
                          ? "success"
                          : selectedMaintenance.status?.toLowerCase() ===
                              "inprogress"
                            ? "warning"
                            : "secondary"
                      }
                    >
                      {selectedMaintenance.status || "Pending"}
                    </Badge>
                  </div>
                </div>

                {/* Content Section */}
                <div className="p-6 space-y-6">
                  {/* Asset Information Card */}
                  <div className="bg-secondary/20 p-4 rounded-lg border">
                    <h3 className="text-sm font-medium text-primary mb-3 flex items-center gap-2">
                      <Box className="w-4 h-4" /> Asset Details
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <span className="text-xs text-muted-foreground uppercase tracking-wider block mb-1">
                          Asset Code
                        </span>
                        <div className="flex items-center gap-2 font-medium">
                          <Hash className="w-4 h-4 text-muted-foreground" />
                          <span>{selectedMaintenance.assetCode}</span>
                        </div>
                      </div>
                      <div>
                        <span className="text-xs text-muted-foreground uppercase tracking-wider block mb-1">
                          Asset Name
                        </span>
                        <div className="flex items-center gap-2 font-medium">
                          <Box className="w-4 h-4 text-muted-foreground" />
                          <span>{selectedMaintenance.assetName}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Maintenance Schedule Card */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <span className="text-xs text-muted-foreground uppercase tracking-wider flex items-center gap-1 mb-2">
                        <RefreshCw className="w-3 h-3" /> Schedule (Month)
                      </span>
                      <p className="font-medium">
                        {selectedMaintenance.repeat || "N/A"}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground uppercase tracking-wider flex items-center gap-1 mb-2">
                        <Calendar className="w-3 h-3" /> Start Date
                      </span>
                      <p className="font-medium">
                        {formatDate(selectedMaintenance.startDate)}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground uppercase tracking-wider flex items-center gap-1 mb-2">
                        <Calendar className="w-3 h-3" /> End Date
                      </span>
                      <p className="font-medium">
                        {formatDate(selectedMaintenance.endDate)}
                      </p>
                    </div>
                  </div>

                  <Separator />

                  {/* Location Information */}
                  <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
                    <div>
                      <span className="text-xs text-muted-foreground uppercase tracking-wider flex items-center gap-1 mb-2">
                        <Building2 className="w-3 h-3" /> Location
                      </span>
                      <p className="font-medium">
                        {getAcademyName(selectedMaintenance.academyID)}
                      </p>
                    </div>
                  </div>

                  {/* Maintenance Description */}
                  <div className="pt-2">
                    <span className="text-xs text-muted-foreground uppercase tracking-wider flex items-center gap-1 mb-2">
                      <FileText className="w-3 h-3" /> Description
                    </span>
                    <div className="bg-muted min-h-[100px] p-4 rounded-md text-sm border shadow-sm leading-relaxed whitespace-pre-wrap">
                      {selectedMaintenance.description ||
                        "No description provided."}
                    </div>
                  </div>
                </div>

                {/* Footer Section */}
                <div className="bg-muted/50 px-6 py-4 border-t flex justify-end">
                  <Button
                    variant="outline"
                    onClick={() => setIsViewModalOpen(false)}
                  >
                    Close
                  </Button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminMaintenance;
